/**
 * /leads/reports - the Lead Desk's reports, moved off the working screen: pipeline summary, funnel and analytics (the existing
 * FunnelTab and AnalyticsTab over the workspace's leads). Clicking a lead in the funnel opens it on /leads.
 */
import { lazy, Suspense, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, MessageSquare } from "lucide-react";
import type { Lead } from "@shared/schema";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { getJson } from "./api";

const FunnelTab = lazy(() => import("@/pages/lead-desk/FunnelTab"));
const AnalyticsTab = lazy(() => import("@/pages/lead-desk/AnalyticsTab"));

interface Pipeline { total: number; byStage: Record<string, number>; overdueFollowUps: number; hottestLead: { id: number; name: string; score: number } | null; activeLeads: number; estimatedPipelineValue: number }

const Fallback = () => <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" /></div>;

export default function LeadReportsPage() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const [, setLocation] = useLocation();

  const { data: leads, isLoading, isError, error } = useQuery<Lead[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "leads", "reports"],
    queryFn: () => getJson(`/api/workspaces/${activeWorkspaceId}/leads?limit=500`),
    enabled: !!activeWorkspaceId,
    staleTime: 60_000,
  });
  const { data: pipeline } = useQuery<Pipeline>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "rogers", "pipeline"],
    queryFn: () => getJson(`/api/workspaces/${activeWorkspaceId}/rogers/pipeline`),
    enabled: !!activeWorkspaceId,
    staleTime: 60_000,
  });
  // the analytics cards count by the old status values; legacy lowercase rows count with their uppercase twins
  const statusCounts = useMemo(() => {
    const c = { NEW: 0, CONTACTED: 0, QUALIFIED: 0, CONVERTED: 0 };
    for (const l of leads ?? []) { const s = String(l.status || "").toUpperCase() as keyof typeof c; if (s in c) c[s]++; }
    return c;
  }, [leads]);
  const normalized = useMemo(() => (leads ?? []).map((l) => ({ ...l, status: String(l.status || "NEW").toUpperCase() })), [leads]);

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <MessageSquare size={48} className="text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground text-sm">Pick a workspace to see its reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-reports-page">
      <div className="space-y-1">
        <Link href="/leads" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="link-back-to-leads"><ArrowLeft size={14} /> Leads</Link>
        <h1 className="text-3xl font-display font-bold text-foreground">Lead reports</h1>
      </div>

      {pipeline && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pipeline-summary">
          <Stat label="Leads" value={pipeline.total} testId="stat-total" />
          <Stat label="Still open" value={pipeline.activeLeads} testId="stat-active" />
          <Stat label="Follow-ups overdue" value={pipeline.overdueFollowUps} testId="stat-overdue" />
          <Stat label="Rough pipeline value" value={`$${Math.round(pipeline.estimatedPipelineValue).toLocaleString()}`} testId="stat-value" />
        </div>
      )}

      {isError ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400" data-testid="reports-error">{(error as Error)?.message}</div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="font-display font-semibold text-foreground">Funnel</h2>
            <Suspense fallback={<Fallback />}>
              <FunnelTab leads={normalized as Lead[]} isLoading={isLoading} onSelectLead={(l) => setLocation(`/leads?lead=${l.id}`)} />
            </Suspense>
          </section>
          <section className="space-y-3">
            <h2 className="font-display font-semibold text-foreground">Analytics</h2>
            <Suspense fallback={<Fallback />}>
              <AnalyticsTab leads={normalized as Lead[]} isLoading={isLoading} statusCounts={statusCounts} />
            </Suspense>
          </section>
          {(leads?.length ?? 0) >= 500 && <p className="text-xs text-muted-foreground">Reports cover your 500 most recent leads.</p>}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, testId }: { label: string; value: number | string; testId: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card/50 px-4 py-3" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-display font-bold text-foreground">{value}</div>
    </div>
  );
}
